import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { DateComponent } from '../date/date.component';
import { ComponentSize } from '../types';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-date-range',
  imports: [CommonModule, FormsModule, DateComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div class="flex items-center gap-3">
        <div class="flex-1">
          <app-date
            [label]="startLabel()"
            [placeholder]="startPlaceholder()"
            [disabled]="disabled()"
            [required]="required()"
            [ngModel]="startDateString()"
            (ngModelChange)="onStartDateChange($event)" />
        </div>
        
        <div class="text-gray-400 dark:text-gray-500">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
        
        <div class="flex-1">
          <app-date
            [label]="endLabel()"
            [placeholder]="endPlaceholder()"
            [disabled]="disabled()"
            [required]="required()"
            [ngModel]="endDateString()"
            [minDate]="startDateString()"
            (ngModelChange)="onEndDateChange($event)" />
        </div>
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class DateRangeComponent implements ControlValueAccessor {
  label = input<string>('');
  startLabel = input<string>('Start Date');
  endLabel = input<string>('End Date');
  startPlaceholder = input<string>('Select start date');
  endPlaceholder = input<string>('Select end date');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  size = input<ComponentSize>('md');

  valueChange = output<DateRange>();

  startDate = signal<Date | null>(null);
  endDate = signal<Date | null>(null);
  touched = signal(false);

  private onChange = (value: DateRange) => {};
  private onTouched = () => {};

  startDateString(): string {
    return this.startDate() ? this.formatDate(this.startDate()!) : '';
  }

  endDateString(): string {
    return this.endDate() ? this.formatDate(this.endDate()!) : '';
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  parseDate(dateString: string): Date | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  onStartDateChange(value: string | Date | null): void {
    const date = typeof value === 'string' ? this.parseDate(value) : value;
    this.startDate.set(date);
    if (date && this.endDate() && date > this.endDate()!) {
      this.endDate.set(null);
    }
    this.updateValue();
    this.markAsTouched();
  }

  onEndDateChange(value: string | Date | null): void {
    const date = typeof value === 'string' ? this.parseDate(value) : value;
    this.endDate.set(date);
    this.updateValue();
    this.markAsTouched();
  }

  private updateValue(): void {
    const range: DateRange = {
      start: this.startDate(),
      end: this.endDate()
    };
    this.onChange(range);
    this.valueChange.emit(range);
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: DateRange | null): void {
    if (value) {
      this.startDate.set(value.start);
      this.endDate.set(value.end);
    } else {
      this.startDate.set(null);
      this.endDate.set(null);
    }
  }

  registerOnChange(fn: (value: DateRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

